import type { ProcessSection, ProcessStatus, ProcessWorkflow } from "../../api";
import type { FieldRef, Problem, StageDraft } from "./types";

/** Assignment modes that resolve their approvers from a group. */
const NEEDS_POOL = ["ManualFromPool", "AutoQuorum", "AutoAll"];

/** Modes that read the org chart instead of a group. They need no pool, and
 *  the settings they DO need are checked below -- a slot whose unit does not
 *  exist is a step no case can get past. */
const DERIVED = ["AutoManager", "AutoUnitHead"];

type Input = {
  stages: StageDraft[];
  sections: Record<number, ProcessSection>;
  workflows: ProcessWorkflow[];
  statuses: ProcessStatus[];
  fields: FieldRef[];
};

/** Every field a rule mentions, however the rule is nested. */
export const fieldsUsedBy = (rule: any, into: string[] = []): string[] => {
  if (!rule || typeof rule !== "object") return into;
  if (typeof rule.field === "string") {
    into.push(rule.field);
    // A threshold's value may name another field rather than be a literal
    // ("end date is after start date"). That side is read at the same moment
    // as the left one, so it earns the same checks — it was silently skipped
    // while a leaf returned as soon as it found its own `field`.
    const { value } = rule;
    if (value && typeof value === "object") {
      (Array.isArray(value) ? value : [value]).forEach((v: any) => {
        if (v && typeof v.field === "string") into.push(v.field);
      });
    }
    return into;
  }
  ["all", "any"].forEach((join) => {
    if (Array.isArray(rule[join])) {
      rule[join].forEach((r: any) => fieldsUsedBy(r, into));
    }
  });
  if (rule.not) fieldsUsedBy(rule.not, into);
  return into;
};

/**
 * Everything wrong with the draft, found in the browser.
 *
 * The server validates too, and it is the authority — but it only answers when
 * you press Save, which turns "I made a typo" into a round trip, a scrolled
 * page and a list of strings to match against the outline by hand. These are
 * the same checks, reported against the node they belong to so the outline can
 * point straight at them.
 *
 * Errors are things the server will refuse. Warnings are legal but almost
 * always a mistake, so they are shown and never block.
 */
export const validate = ({
  stages,
  sections,
  workflows,
  statuses,
  fields,
}: Input): Problem[] => {
  const problems: Problem[] = [];
  const byName = new Map(fields.map((f) => [f.name, f]));
  const stageName = (stage: StageDraft, index: number) =>
    stage.name.trim() || `#${index + 1}`;

  stages.forEach((stage, index) => {
    const target = { type: "stage" as const, key: stage.key };
    const name = stageName(stage, index);

    if (!stage.name.trim()) {
      problems.push({
        severity: "error",
        message: "Step {{stage}} has no name.",
        params: { stage: name },
        target,
      });
    }

    const contents =
      stage.kind === "DataEntry" ? stage.form_ids : stage.workflow_ids;
    if (contents.length === 0) {
      problems.push({
        severity: "warning",
        message:
          stage.kind === "DataEntry"
            ? "{{stage}} has no sections, so a case passes straight through it."
            : "{{stage}} has no sign-offs, so a case passes straight through it.",
        params: { stage: name },
        target,
      });
    }

    // A data-entry stage where nothing is required cannot hold a permit up,
    // which is rarely what someone building a stage intended.
    if (
      stage.kind === "DataEntry" &&
      stage.form_ids.length > 0 &&
      stage.form_ids.every((id) => sections[id]?.is_required === false)
    ) {
      problems.push({
        severity: "warning",
        message: "Every section in {{stage}} is optional, so nothing holds it open.",
        params: { stage: name },
        target,
      });
    }

    fieldsUsedBy(stage.condition).forEach((fieldName) => {
      const field = byName.get(fieldName);
      if (!field) {
        problems.push({
          severity: "error",
          message: "{{stage}} has a rule about “{{field}}”, which no section asks.",
          params: { stage: name, field: fieldName },
          target,
        });
        return;
      }
      // The rule is evaluated when a permit reaches this stage, so a field that
      // is not answered until this stage or a later one has no value yet — the
      // rule is valid, saves cleanly, and can never be true.
      if (field.stageIndex >= index) {
        problems.push({
          severity: "error",
          message:
            "{{stage}} has a rule about “{{field}}”, which is not answered until {{where}}.",
          params: {
            stage: name,
            field: field.label || fieldName,
            where:
              field.stageIndex === index
                ? name
                : stageName(stages[field.stageIndex], field.stageIndex),
          },
          target,
        });
      }
    });
  });

  Object.values(sections).forEach((section) => {
    if (!section.title.trim()) {
      problems.push({
        severity: "error",
        message: "A section has no name.",
        target: { type: "section", id: section.id },
      });
    }
    if (section.fill_permission === "Group" && !section.fill_group) {
      problems.push({
        severity: "error",
        message: "“{{section}}” is filled by a group, but no group is chosen.",
        params: { section: section.title },
        target: { type: "section", id: section.id },
      });
    }
  });

  workflows.forEach((workflow) => {
    const target = { type: "workflow" as const, id: workflow.id as number };
    const name = workflow.name?.trim();

    if (!name) {
      problems.push({
        severity: "error",
        message: "A sign-off has no name.",
        target,
      });
    }

    workflow.roles?.forEach((role) => {
      if (!role.title?.trim()) {
        problems.push({
          severity: "error",
          message: "A signature slot on “{{workflow}}” has no name.",
          params: { workflow: name || "—" },
          target,
        });
      }
      const isDerived = DERIVED.includes(role.assignment);
      if (!isDerived && NEEDS_POOL.includes(role.assignment) && !role.pool) {
        problems.push({
          severity: "error",
          message:
            "“{{slot}}” takes its approvers from a group, but no group is chosen.",
          params: { slot: role.title?.trim() || "—" },
          target,
        });
      }
      if (role.assignment === "AutoUnitHead") {
        const resolver = role.resolver || {};
        const source = resolver.unit_source || "requester";
        if (source === "field" && !String(resolver.field || "").trim()) {
          problems.push({
            severity: "error",
            message:
              "“{{slot}}” takes its approver from a unit named in an answer, but no question is chosen.",
            params: { slot: role.title?.trim() || "—" },
            target,
          });
        }
        if (source === "fixed" && !String(resolver.unit || "").trim()) {
          problems.push({
            severity: "error",
            message: "“{{slot}}” has no unit chosen.",
            params: { slot: role.title?.trim() || "—" },
            target,
          });
        }
      }
    });

    if ((workflow.roles?.length ?? 0) === 0 && (workflow.approvers_count ?? 0) < 1) {
      problems.push({
        severity: "error",
        message: "“{{workflow}}” needs at least one approver.",
        params: { workflow: name || "—" },
        target,
      });
    }
  });

  const kindCount = (kind: string) =>
    statuses.filter((s) => s.kind === kind).length;
  if (kindCount("Draft") !== 1) {
    problems.push({
      severity: "error",
      message:
        "Exactly one state has to be of kind ‘Being written’ — that is where a new case starts.",
      target: null,
    });
  }
  if (kindCount("Approval") > 1) {
    problems.push({
      severity: "error",
      message: "Only one state can be of kind ‘Being signed off’.",
      target: null,
    });
  }
  if (statuses.some((s) => !s.name.trim())) {
    problems.push({
      severity: "error",
      message: "A state has no name.",
      target: null,
    });
  }

  // Anything in no stage at all: legal, and occasionally deliberate while a
  // process is being rearranged, but a permit will never reach it.
  const placedForms = new Set(stages.flatMap((s) => s.form_ids));
  const placedWorkflows = new Set(stages.flatMap((s) => s.workflow_ids));
  if (stages.length > 0) {
    Object.values(sections)
      .filter((s) => !placedForms.has(s.id))
      .forEach((section) =>
        problems.push({
          severity: "warning",
          message: "“{{section}}” is in no step, so a case never reaches it.",
          params: { section: section.title },
          target: { type: "section", id: section.id },
        })
      );
    workflows
      .filter((w) => w.id != null && !placedWorkflows.has(w.id))
      .forEach((workflow) =>
        problems.push({
          severity: "warning",
          message: "“{{workflow}}” is in no step, so a case never reaches it.",
          params: { workflow: workflow.name?.trim() || "—" },
          target: { type: "workflow", id: workflow.id as number },
        })
      );
  }

  return problems;
};

/** Problems belonging to one node, for its badge in the outline. */
export const problemsFor = (
  problems: Problem[],
  match: { type: "stage"; key: string } | { type: "section" | "workflow"; id: number }
): Problem[] =>
  problems.filter((p) => {
    if (!p.target || p.target.type !== match.type) return false;
    if (p.target.type === "stage" && match.type === "stage") {
      return p.target.key === match.key;
    }
    return (p.target as { id: number }).id === (match as { id: number }).id;
  });

/** The worst severity present, or null when there is nothing wrong. */
export const worstOf = (problems: Problem[]): "error" | "warning" | null => {
  if (problems.some((p) => p.severity === "error")) return "error";
  if (problems.length > 0) return "warning";
  return null;
};
